<template>
  <div v-if="customFieldListItems.length > 0 || loading" class="custom-field-filters">
    <WdsStack justify-space-between align-center>
      <WdsText secondary>
        {{ $t('Custom fields').toUpperCase() }}
      </WdsText>
      <WdsButtonGhost
        :disable="customFields.length === 0 || loading"
        secondary
        @click="addCustomFieldFilter"
      >
        <WdsStack gap-xs align-center>
          <WdsIcon name="plus" :size="16" />
          {{ $t('Create filter') }}
        </WdsStack>
      </WdsButtonGhost>
    </WdsStack>
    <WdsDivider class="tw-mt-0.5" />
    <div v-if="!loading">
      <WdsStack
        v-for="fieldFilter in customFieldFilters"
        :key="fieldFilter.key"
        class="tw-w-full"
        direction-column
        align-start
        gap-sm
      >
        <WdsText class="tw-w-full" :sub-text="customFieldLookup[fieldFilter.id].description">
          <WdsStack justify-space-between align-center class="tw-mt-2.5">
            <span>
              {{ customFieldLookup[fieldFilter.id].title }}
            </span>
            <WdsButtonIcon transparent-background sm @click="removeCustomFieldFilter(fieldFilter)">
              <WdsIcon name="trash-can" weight="thick" :size="16" />
            </WdsButtonIcon>
          </WdsStack>
        </WdsText>
        <CustomFieldSingleSelect
          v-if="fieldFilter.value.singleSelect"
          v-model="fieldFilter.value.singleSelect.value"
          :list="customFieldLookup[fieldFilter.id].customFieldOptions"
        />
        <CustomFieldMultiSelect
          v-else-if="fieldFilter.value.multiSelect"
          v-model="fieldFilter.value.multiSelect.values"
          :list="customFieldLookup[fieldFilter.id].customFieldOptions"
        />
        <WdsSelect
          v-else-if="fieldFilter.value.boolean"
          class="tw-w-full"
          :list="booleanOptions"
          :model-value="booleanOptions.find(({ value }) => value === fieldFilter.value.boolean?.value)"
          :placeholder="$t('Select option to filter by')"
          @update:model-value="setBooleanFilter(fieldFilter.value, $event)"
        />
        <WdsInputDate
          v-else-if="fieldFilter.value.date"
          v-model="fieldFilter.value.date.value"
          class="tw-w-full"
          format="yyyy-mm-dd"
        />
        <WdsInput
          v-else-if="fieldFilter.value.freeText"
          v-model="fieldFilter.value.freeText.value"
          :placeholder="$t('Text to search for')"
        />
      </WdsStack>
    </div>
    <WdsStack v-if="addFilterOpen" class="tw-w-full" direction-column align-start gap-sm>
      <WdsText class="tw-w-full">
        <WdsStack justify-space-between align-center class="tw-mt-2.5">
          <span>
            {{ $t('Select a field to filter by') }}
          </span>
          <WdsButtonIcon transparent-background sm @click="addFilterOpen = false">
            <WdsIcon name="trash-can" weight="thick" :size="16" />
          </WdsButtonIcon>
        </WdsStack>
      </WdsText>
      <WdsSelect
        class="tw-w-full"
        :list="customFieldListItems"
        :placeholder="$t('Custom fields')"
        @update:model-value="fieldSelected($event)"
      />
    </WdsStack>
  </div>
</template>

<script setup lang="ts">
import {
  WdsButtonGhost,
  WdsButtonIcon,
  WdsDivider,
  WdsIcon,
  WdsInput,
  WdsInputDate,
  WdsSelect,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { computed, ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import uniqBy from 'lodash/uniqBy'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import {
  CustomFieldFilterFieldsFragment,
  CustomFieldType,
  CustomFieldTypeValueInput,
  TypeSupportingCustomFieldValues,
} from '@/gql/graphql'
import { uuid } from '@/utils/tools'
import { graphql } from '@/gql'
import { FilterMetaDataProp } from '@/components/GraphqlDataTable/filters/types'
import CustomFieldSingleSelect from '@/components/GraphqlDataTable/filters/CustomFields/CustomFieldSingleSelect.vue'
import CustomFieldMultiSelect from '@/components/GraphqlDataTable/filters/CustomFields/CustomFieldMultiSelect.vue'

const { meta } = defineProps<{ meta: FilterMetaDataProp }>()

// The key here is used for internal tracking, and the id is the custom fields id
const model = defineModel<{ value: CustomFieldTypeValueInput; key: string; id: string }[] | null>({
  default: null,
})

const customFieldFilters = computed(() => model.value ?? [])

graphql(`
  fragment CustomFieldFilterFields on CustomField {
    title
    id
    description
    slug
    fieldType
    appliesTo
    visibility
    recruiterInputAllowed
    customFieldOptions {
      id
      value: id
      displayOrder
      label: value # used for listing purposes
    }
  }
`)

const { result, loading } = useQuery(
  graphql(`
    query CompanyCustomFieldsQuery($companyId: ID!) {
      company(id: $companyId) {
        id
        settings {
          staffingAgencyManagesWorker
        }
        organisations(first: 10000) {
          data {
            id
            divisions(first: 1000) {
              paginatorInfo {
                count
              }
              data {
                id
              }
            }
          }
          paginatorInfo {
            count
          }
        }
        customFields(first: 1000) {
          data {
            ...CustomFieldFilterFields
          }
        }
      }
    }
  `),
  { companyId: currentAccount.globalId },
  {
    clientId: 'publicApiCustomFields',
  },
)

const inputArgToEntityType: Record<string, TypeSupportingCustomFieldValues> = {
  customFields: TypeSupportingCustomFieldValues.Job,
  companyRecruiterCustomFields: TypeSupportingCustomFieldValues.CompanyRecruiter,
  trustedContactCustomFields: TypeSupportingCustomFieldValues.TrustedContact,
}

const targetEntityType = computed(
  () => inputArgToEntityType[meta.filter.inputArgName] ?? TypeSupportingCustomFieldValues.Job,
)

const getFields = (company) => company.customFields?.data

const customFields = computed(() => {
  if (loading.value) return []
  const companyFields = getFields(result.value?.company).flat()

  return uniqBy<any>(
    companyFields.flat().filter((field) => field.appliesTo === targetEntityType.value),
    (field) => field.id,
  )
})

const customFieldLookup = computed(() =>
  // Makes an object of type id: customField to use for lookup
  customFields.value.reduce((acc, customField) => ({ ...acc, [customField.id]: customField }), {}),
)

const customFieldListItems = computed(() =>
  customFields.value.map((customField) => ({ label: customField.title, value: customField })),
)

const booleanOptions = [
  { value: true, label: $t('Yes') },
  { value: false, label: $t('No') },
]

const setBooleanFilter = (
  filterValue: CustomFieldTypeValueInput,
  option: { value: boolean; label: string } | null,
) => {
  if (filterValue.boolean) {
    // eslint-disable-next-line no-param-reassign
    filterValue.boolean.value = option?.value ?? null
  }
}

const addFilterOpen = ref(false)
const addCustomFieldFilter = () => {
  addFilterOpen.value = true
}

const fieldSelected = ({ value }: { value: CustomFieldFilterFieldsFragment }) => {
  addFilterOpen.value = false

  const baseInput = {
    id: value.id,
    slug: value.slug,
  }

  let filterValue: CustomFieldTypeValueInput

  if (value.fieldType === CustomFieldType.MultiSelect) {
    filterValue = {
      freeText: null,
      singleSelect: null,
      multiSelect: { ...baseInput, values: [] },
    }
  } else if (value.fieldType === CustomFieldType.SingleSelect) {
    filterValue = {
      freeText: null,
      singleSelect: { ...baseInput, value: value.customFieldOptions?.[0]?.value },
      multiSelect: null,
    }
  } else if (value.fieldType === CustomFieldType.Boolean) {
    filterValue = { boolean: { ...baseInput, value: true } }
  } else if (value.fieldType === CustomFieldType.Date) {
    filterValue = { date: { ...baseInput, value: null } }
  } else {
    filterValue = {
      freeText: { ...baseInput, value: '' },
      singleSelect: null,
      multiSelect: null,
    }
  }

  const data = {
    value: filterValue,
    key: uuid(),
    id: value.id,
  }

  if (model.value === null) {
    model.value = [data]
  } else {
    model.value.push(data)
  }
}

const removeCustomFieldFilter = (customField) => {
  if (model.value === null) return
  const index = model.value.findIndex(({ key }) => key === customField.key)
  if (index !== -1) {
    model.value.splice(index, 1)
  }
}
</script>

<style scoped lang="scss">
.custom-field-filters {
  :deep(.wds-input-wrapper) {
    width: 100%;
  }
}
</style>
